import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, CanDeactivate, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Observable } from 'rxjs';
import { AuthTimeoutResponse, EntitlementLevel } from './index';
import { groupExists } from './entitlements.util';
import { FipStore } from './fip-state/fip.store';
import { SessionConfig } from './session-config';

@Injectable({
  providedIn: 'root',
})
export class FipSessionService implements CanActivate, CanDeactivate<any> {
  private store = inject(FipStore);

  get userInformation() {
    return this.store.userInformation();
  }
  get currentEnv() {
    return this.store.currentEnv();
  }
  get isShowModal() {
    return this.store.isShowModal();
  }
  get forceLogout() {
    return this.store.forceLogout();
  }
  get remainingTime() {
    return this.store.remainingTime();
  }
  get sessionExists() {
    return this.store.sessionExists();
  }
  get goodSession() {
    return this.store.goodSession();
  }
  get expiring() {
    return this.store.expiring();
  }
  get expiresInMilliSeconds() {
    return this.store.expiresInMilliSeconds();
  }

  canDeactivate(
    component: any,
    currentRoute: ActivatedRouteSnapshot,
    currentState: RouterStateSnapshot,
    nextState?: RouterStateSnapshot,
  ): boolean | UrlTree | Observable<boolean | UrlTree> | Promise<boolean | UrlTree> {
    const fipServiceInformation = {
      userInformation: this.store.userInformation(),
      currentTimeoutInformation: {
        sessionExists: this.store.sessionExists() ? 'Y' : 'N',
        expiresInMilliSeconds: this.store.expiresInMilliSeconds(),
        expirationTime: this.store.expirationTime(),
      } as AuthTimeoutResponse,
    };
    if (this.store.currentEnv()) {
      return (
        currentRoute.data?.sessionDeacctivationFunction?.(
          component,
          currentRoute,
          currentState,
          nextState,
          fipServiceInformation,
        ) ?? this.store.sessionExists()
      );
    }
    return true;
  }

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): boolean | UrlTree | Observable<boolean | UrlTree> | Promise<boolean | UrlTree> {
    const fipServiceInformation = {
      userInformation: this.store.userInformation(),
      currentTimeoutInformation: {
        sessionExists: this.store.sessionExists() ? 'Y' : 'N',
        expiresInMilliSeconds: this.store.expiresInMilliSeconds(),
        expirationTime: this.store.expirationTime(),
      } as AuthTimeoutResponse,
    };

    if (this.store.currentEnv()) {
      const groups: Array<string | { name: string; levels: EntitlementLevel[] }> = route.data.groups;
      return (
        route.data?.sessionActivationFunction?.(route, state, fipServiceInformation) ??
        (this.store.sessionExists() && (route.data.groups?.length ? this.groupExists(route.data.groups) : true))
      );
    }
    return true;
  }
  groupExists(groups: Array<string | { name: string; levels: EntitlementLevel[] }>) {
    const groupData = {
      currentEnv: this.currentEnv,
      groups,
      userInformation: this.store.userInformation(),
      privNameMap: this.store.privNameMap(),
    };
    return groupExists(groupData);
  }
  load(config: SessionConfig) {
    return this.store.load(config);
  }

  async resolveSessionUserAction(extendSession: boolean) {
    this.store.overlayRef().dispose();
    if (extendSession) {
      // call any known endpoint to keep alive
      return await this.store.refreshUserSession(true);
    }

    this.store.logout();
  }
}
